import { useCanEdit } from '../components/EditorOnly';
import { FakesVolumeHistoryDialog } from './FakesVolumeHistoryDialog';
import { FakesVolumeStopButton } from './FakesVolumeStopButton';
import { FakesVolumeWordstatFields } from './FakesVolumeWordstatFields';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  TextField,
  MenuItem,
  Link,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { api } from '../api/client';
import type { FakesMatrixSettings } from '../api/types';
import {
  GOOGLE_VOLUME_LIMIT,
  GOOGLE_VOLUME_LANGUAGES,
  volumeCoverageLabel,
  type GoogleVolumeTarget,
  type VolumeState,
} from '../../../api/src/fakes/query-volume';
import { sameVolumeContext, type VolumeContext } from '../../../api/src/fakes/volume-context';
import type { VolumeCollectionMode, VolumePlan } from '../../../api/src/fakes/volume-plan';
import { Stack, Typography } from '../components/mui-system';
export function FakesVolumesDialog({
  workspaceId,
  settings,
  state,
  error: loadError,
  editable,
  onStarted,
  onClose,
}: {
  workspaceId: string;
  settings: FakesMatrixSettings;
  state: VolumeState | null;
  error: string;
  editable: boolean;
  onStarted(): void;
  onClose(): void;
}) {
  const canStop = useCanEdit('fakes.stop');
  const canSchedule = useCanEdit('fakes.schedule', 'fakes.run');
  const [historyOpen, setHistoryOpen] = useState(false);
  const [busy, setBusy] = useState(false),
    [error, setError] = useState('');
  const [requestId, setRequestId] = useState(() => crypto.randomUUID());
  const [retryRequestId, setRetryRequestId] = useState(() => crypto.randomUUID());
  const [missingRequestId, setMissingRequestId] = useState(() => crypto.randomUUID());
  const [started, setStarted] = useState(false);
  const [scheduleSaved, setScheduleSaved] = useState(false);
  const count = settings.queries.filter((q) => q.active).length;
  const profile = settings.profile;
  const [source, setSource] = useState<VolumeContext['source']>('wordstat');
  const google = source === 'google_ads';
  const sourceState = state?.sources[source];
  const schedule = sourceState?.schedule;
  const configured = sourceState?.configured ?? false;
  const [wordstatDraft, setWordstatDraft] = useState<Extract<
    VolumeContext,
    { source: 'wordstat' }
  > | null>();
  const savedWordstat = state?.sources.wordstat.context;
  const wordstat =
    wordstatDraft === undefined
      ? savedWordstat?.source === 'wordstat'
        ? savedWordstat
        : null
      : wordstatDraft;
  const savedGoogle = state?.sources.google_ads.context;
  const googleTarget = savedGoogle?.source === 'google_ads' ? savedGoogle : null;
  const [regionQuery, setRegionQuery] = useState(profile?.regionName ?? '');
  const [regions, setRegions] = useState<
    Array<{ id: string; name: string; countryCode: string; type: string }>
  >([]);
  const [picked, setPicked] = useState<{ id: string; name: string } | null | undefined>();
  const [language, setLanguage] = useState<GoogleVolumeTarget['languageId']>();
  const [searched, setSearched] = useState(false);
  const selectedRegion =
    picked === undefined && googleTarget
      ? { id: googleTarget.geoTargetId, name: googleTarget.regionName }
      : picked;
  const languageId = language ?? googleTarget?.languageId ?? 'all';
  const regionOptions =
    selectedRegion && !regions.some((r) => r.id === selectedRegion.id)
      ? [selectedRegion, ...regions]
      : regions;
  const context: VolumeContext | null = google
    ? selectedRegion
      ? {
          source: 'google_ads',
          geoTargetId: selectedRegion.id,
          regionName: selectedRegion.name,
          languageId,
        }
      : null
    : wordstat;
  const contextJson = JSON.stringify(context);
  useEffect(() => {
    setRequestId(crypto.randomUUID());
    setRetryRequestId(crypto.randomUUID());
    setMissingRequestId(crypto.randomUUID());
  }, [contextJson]);
  const planKey = JSON.stringify([workspaceId, settings.revision, context, state?.latest]);
  const [planResult, setPlanResult] = useState<{
    key: string;
    data?: VolumePlan;
    error?: string;
  }>();
  const [planRevision, setPlanRevision] = useState(0);
  const plan = planResult?.key === planKey ? planResult.data : undefined;
  const planError = planResult?.key === planKey ? planResult.error : undefined;
  useEffect(() => {
    const selected: VolumeContext | null = JSON.parse(contextJson);
    if (!selected) return;
    let cancelled = false;
    api.planFakesVolumes(workspaceId, { revision: settings.revision, context: selected }).then(
      (data) => {
        if (!cancelled) setPlanResult({ key: planKey, data });
      },
      (e) => {
        if (!cancelled)
          setPlanResult({
            key: planKey,
            error: e instanceof Error ? e.message : 'Не удалось рассчитать обращения',
          });
      },
    );
    return () => {
      cancelled = true;
    };
  }, [workspaceId, settings.revision, contextJson, planKey, planRevision]);
  const saveSettings = async (enabled: boolean, saveContext = true) => {
    if (!schedule) return;
    setBusy(true);
    setError('');
    setScheduleSaved(false);
    try {
      await api.saveFakesVolumeSchedule(workspaceId, {
        source,
        enabled,
        scheduleRevision: schedule.revision,
        ...(saveContext && context ? { context, revision: settings.revision } : {}),
      });
      setScheduleSaved(true);
      onStarted();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось сохранить настройки частотности');
    } finally {
      setBusy(false);
    }
  };
  const collect = async (mode: VolumeCollectionMode) => {
    if (!plan?.[mode]) return;
    setBusy(true);
    setError('');
    try {
      await api.collectFakesVolumes(workspaceId, {
        requestId:
          mode === 'failed' ? retryRequestId : mode === 'missing' ? missingRequestId : requestId,
        context: context!,
        revision: settings.revision,
        ...(mode === 'failed'
          ? { retryFailed: true }
          : mode === 'missing'
            ? { onlyMissing: true }
            : {}),
        plannedRequests: plan[mode]!.requests,
      });
      setStarted(true);
      onStarted();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось запустить сбор');
    } finally {
      setBusy(false);
    }
  };
  const onStopped = () => {
    setStarted(false);
    setRequestId(crypto.randomUUID());
    setRetryRequestId(crypto.randomUUID());
    setMissingRequestId(crypto.randomUUID());
    onStarted();
  };
  return (
    <Dialog open onClose={busy ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>Частотность запросов</DialogTitle>
      <DialogContent>
        <Stack gap={2} pt={1}>
          <Alert severity="info">
            Вес запросов для выдачи Яндекса и Google — частотность Яндекса (Wordstat). Частотность
            Google пока не используется для веса.
          </Alert>
          <Button onClick={() => setHistoryOpen(true)}>История пакетов</Button>
          {state?.latest && state.latest.pending > 0 && !state.latest.stop && (
            <Typography fontWeight={600}>
              Текущий сбор:{' '}
              {state.latest.context.source === 'wordstat'
                ? 'Яндекс · Wordstat'
                : 'Google · Keyword Planner'}{' '}
              · {state.latest.context.regionName} ·{' '}
              {state.latest.context.source === 'wordstat'
                ? state.latest.context.device === 'mobile'
                  ? 'телефоны'
                  : 'компьютеры'
                : GOOGLE_VOLUME_LANGUAGES[state.latest.context.languageId]}
            </Typography>
          )}
          {state?.latest && canStop && (
            <FakesVolumeStopButton
              workspaceId={workspaceId}
              batch={state.latest}
              disabled={busy}
              onStopped={onStopped}
            />
          )}
          {state?.latest?.stop && state.latest.pending > 0 && (
            <Alert severity="info">
              Останавливаем сбор: ждём результат начатого обращения. Новые обращения этого пакета не
              запускаются.
            </Alert>
          )}
          <TextField
            select
            label="Источник частотности"
            value={source}
            disabled={busy || started}
            onChange={(event) => {
              setSource(event.target.value as VolumeContext['source']);
              setScheduleSaved(false);
              setError('');
              setRequestId(crypto.randomUUID());
            }}
          >
            <MenuItem value="wordstat">Яндекс · Wordstat</MenuItem>
            <MenuItem value="google_ads">Google · Keyword Planner</MenuItem>
          </TextField>
          {!google && (
            <FakesVolumeWordstatFields
              workspaceId={workspaceId}
              context={wordstat}
              disabled={!editable || busy || started}
              onChange={(value) => {
                setWordstatDraft(value);
                setRequestId(crypto.randomUUID());
                setScheduleSaved(false);
              }}
            />
          )}
          {google && (
            <>
              <Link href="/docs/google-keyword-planner">Инструкция подключения Google Ads</Link>
              <TextField
                label="Найти регион Google"
                value={regionQuery}
                disabled={!editable || busy || started}
                onChange={(e) => {
                  setRegionQuery(e.target.value);
                  setPicked(null);
                  setRegions([]);
                  setSearched(false);
                  setRequestId(crypto.randomUUID());
                }}
              />
              <Button
                disabled={
                  !editable || !configured || busy || started || regionQuery.trim().length < 2
                }
                onClick={async () => {
                  setBusy(true);
                  setError('');
                  try {
                    setRegions(await api.fakesVolumeRegions(workspaceId, regionQuery));
                    setSearched(true);
                  } catch (e) {
                    setError(e instanceof Error ? e.message : 'Не удалось найти регион');
                  } finally {
                    setBusy(false);
                  }
                }}
              >
                Найти регион
              </Button>
              {searched && !regions.length && (
                <Alert severity="info">Доступные регионы не найдены. Уточните название.</Alert>
              )}
              <TextField
                select
                label="Регион частотности Google"
                value={selectedRegion?.id ?? ''}
                disabled={!editable || busy || started || !regionOptions.length}
                onChange={(e) => {
                  setPicked(regionOptions.find((r) => r.id === e.target.value) ?? null);
                  setRequestId(crypto.randomUUID());
                }}
                helperText="Выберите нужный город, область или страну; регион выдачи автоматически не меняется."
              >
                {regionOptions.map((r) => (
                  <MenuItem key={r.id} value={r.id}>
                    {r.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label="Язык запросов Google"
                value={languageId}
                disabled={!editable || busy || started}
                onChange={(e) => {
                  setLanguage(e.target.value as GoogleVolumeTarget['languageId']);
                  setRequestId(crypto.randomUUID());
                }}
              >
                {Object.entries(GOOGLE_VOLUME_LANGUAGES).map(([id, name]) => (
                  <MenuItem key={id} value={id}>
                    {name}
                  </MenuItem>
                ))}
              </TextField>
              {count > GOOGLE_VOLUME_LIMIT && (
                <Alert severity="warning">
                  Google допускает до 10 000 активных запросов за запуск. Уменьшите список перед
                  сбором.
                </Alert>
              )}
            </>
          )}
          <Typography color="text.secondary">
            У каждого источника свои параметры и месячное расписание. Переключение профиля карты их
            не меняет.
          </Typography>
          <Typography>
            {google
              ? 'Google Keyword Planner: приблизительная частотность в месяц, средняя за 12 месяцев. Близкие варианты объединяются; общий вес делится поровну между активными вариантами на карте (в истории — между вариантами того запуска). Для сохранения этих групп повторный сбор выполняется для всего списка.'
              : 'Точная частотность Wordstat за последние 30 дней. Кавычки исключают дополнительные слова, «!» фиксирует формы слов. Порядок слов свободный: перестановки собираются одним обращением, их общий вес делится поровну между активными фразами на карте.'}
          </Typography>
          <Typography>
            {count} активных запросов во всех группах ·{' '}
            {google
              ? (selectedRegion?.name ?? 'Регион Google Ads не выбран')
              : (wordstat?.regionName ?? 'Регион Яндекса не выбран')}{' '}
            ·{' '}
            {google
              ? 'частотность по всем устройствам'
              : wordstat?.device === 'mobile'
                ? 'телефоны'
                : 'компьютеры'}
            .
          </Typography>
          <Typography color="text.secondary">
            {google
              ? 'Поиск региона и сбор расходуют квоту Google Ads. Регион для частотности выберите в справочнике Google.'
              : 'Проверка оплачивается по тарифу Yandex Search API.'}{' '}
            Сбор выдачи не запускается. Частотность можно обновлять вручную или раз в месяц по
            расписанию.
          </Typography>
          {!state && !loadError && <LinearProgress />}
          {plan?.coverage && (
            <Typography>
              {volumeCoverageLabel(plan.coverage)} · активные запросы выбранного контекста во всех
              группах.
            </Typography>
          )}
          {context && !plan && !planError && (
            <Typography color="text.secondary">
              Проверяем полноту данных выбранного контекста…
            </Typography>
          )}
          {google && !selectedRegion && (
            <Typography color="text.secondary">
              Выберите регион Google, чтобы увидеть полноту данных.
            </Typography>
          )}
          {loadError && <Alert severity="error">{loadError}</Alert>}
          {state && !configured && (
            <Alert severity="warning">
              {google
                ? 'Нужно подключение Google Ads API с доступом Basic или Standard и разрешением на Keyword Planner.'
                : 'Для Wordstat нужно подключение Yandex Search API с доступом к статистике запросов.'}{' '}
              Обратитесь к администратору.
            </Alert>
          )}
          {!google && (
            <Typography fontSize={12} color="text.secondary">
              Карта Яндекса показывает частотность для региона и устройства своего профиля. Для
              карты Google используются сохранённые здесь параметры Wordstat; если они ещё не
              сохранены — параметры последнего сбора Wordstat. Регион и устройство выдачи Google
              могут отличаться от параметров частотности Яндекса.
            </Typography>
          )}
          <Typography fontSize={12} color="text.secondary">
            {google
              ? 'Google получает весь список одним пакетом и объединяет близкие варианты в ответе. Дополнительно выполняется проверка региона и, при необходимости, авторизация. Поиск региона выше тоже расходует квоту.'
              : '«Собрать недостающие» пропускает известные значения, включая нули и устаревшие измерения. «Повторить ошибки» выбирает ошибки последней попытки и приостановленные фразы. Предварительный расчёт не обращается к провайдеру.'}
          </Typography>
          {schedule && (
            <Stack gap={1}>
              <Button
                disabled={!canSchedule || busy || !context || started}
                onClick={() => saveSettings(schedule.enabled)}
              >
                Сохранить параметры частотности
              </Button>
              <FormControlLabel
                label="Обновлять частотность ежемесячно"
                control={
                  <Switch
                    checked={schedule.enabled}
                    disabled={
                      !canSchedule ||
                      busy ||
                      (!schedule.enabled &&
                        (!configured ||
                          !context ||
                          !count ||
                          (google && count > GOOGLE_VOLUME_LIMIT)))
                    }
                    onChange={(_, enabled) => saveSettings(enabled, enabled)}
                  />
                }
              />
              <Typography fontSize={12} color="text.secondary">
                Первый запуск — через календарный месяц после включения, далее ежемесячно.
                Собираются все активные запросы всех групп. Полный ручной сбор с теми же параметрами
                переносит следующий запуск на месяц. Повтор ошибок и сбор недостающих дату не
                меняют. Wordstat оплачивается, Google расходует квоту. Измените параметры и
                сохраните их: для включённого расписания первый запуск в новом контексте будет через
                месяц.
              </Typography>
              {schedule.enabled && (
                <Typography fontSize={12}>
                  {sameVolumeContext(schedule.context, context)
                    ? 'Сохранённый контекст частотности совпадает с выбранным. Профиль выдачи на расписание не влияет.'
                    : 'Параметры изменены, но ещё не сохранены. Расписание продолжит работать с сохранённым контекстом.'}
                  {schedule.context?.source === 'google_ads'
                    ? ` Регион Google: ${schedule.context.geoTargetId}; язык: ${GOOGLE_VOLUME_LANGUAGES[schedule.context.languageId]}.`
                    : ''}
                </Typography>
              )}
              {schedule.nextRunAt && (
                <Typography>
                  Следующее обновление: {new Date(schedule.nextRunAt).toLocaleString('ru-RU')}{' '}
                  (время браузера).
                </Typography>
              )}
              {schedule.error && <Alert severity="warning">{schedule.error}</Alert>}
              {scheduleSaved && (
                <Alert severity="success">Параметры и расписание частотности сохранены.</Alert>
              )}
            </Stack>
          )}
          {state?.latest && (
            <Typography>
              Последний запуск
              {state.latest.retryFailed
                ? ' (повтор ошибок)'
                : state.latest.onlyMissing
                  ? ' (сбор недостающих)'
                  : ''}
              : {new Date(state.latest.createdAt).toLocaleString('ru-RU')} · готово{' '}
              {state.latest.complete} из {state.latest.total} · ошибок {state.latest.failed} · в
              работе {state.latest.pending} · приостановлено {state.latest.paused ?? 0}.
              {!sameVolumeContext(state.latest.context, context)
                ? ' Этот запуск относится к другому контексту частотности.'
                : ''}
            </Typography>
          )}
          {state?.latest?.stop && (
            <Alert severity="warning">
              {state.latest.stop.kind === 'user' ? 'Пакет остановлен: ' : 'Пакет приостановлен: '}
              {state.latest.stop.message} Не отправлено фраз: {state.latest.paused}.
              {state.latest.stop.retryAt
                ? ` Провайдер указал время повтора: ${new Date(state.latest.stop.retryAt).toLocaleString('ru-RU')}.`
                : ''}{' '}
              {sameVolumeContext(state.latest.context, context)
                ? google
                  ? 'После устранения причины запустите сбор всего списка заново.'
                  : 'После устранения причины нажмите «Повторить ошибки»: новый пакет включит ошибки и приостановленные фразы.'
                : 'Повтор доступен в исходном контексте частотности этого пакета.'}{' '}
              Автоматического повтора нет.
            </Alert>
          )}
          {state?.latest?.failed ? (
            <Alert severity="warning">
              Ошибки показаны у запросов. Последние успешные измерения сохранены. Неизвестная
              частотность не считается нулём; автоматического повтора обращений к провайдеру нет.
            </Alert>
          ) : null}
          {error && <Alert severity="error">{error}</Alert>}
          {started && (
            <Alert severity="success">
              Сбор частотности запущен. Можно закрыть окно: измерения сохранятся у фраз и будут
              доступны на карте соответствующего источника и контекста.
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <Stack gap={1} sx={{ px: 3, py: 1, borderTop: 1, borderColor: 'divider' }}>
        {context && !plan && !planError && <LinearProgress aria-label="Расчёт обращений" />}
        {plan && (
          <Stack gap={0.5}>
            <Typography fontWeight={600}>Перед запуском</Typography>
            <Typography fontSize={14}>
              Весь список: фраз — {plan.all.queries}, обращений — {plan.all.requests}{' '}
              {google ? 'за частотностью' : 'к Wordstat после объединения перестановок'}.
            </Typography>
            {plan.missing && (
              <Typography fontSize={14}>
                Недостающие: фраз — {plan.missing.queries}, обращений — {plan.missing.requests}.
              </Typography>
            )}
            {plan.failed && (
              <Typography fontSize={14}>
                Ошибки и приостановленные: фраз — {plan.failed.queries}, обращений —{' '}
                {plan.failed.requests}.
              </Typography>
            )}
            {!!plan.all.invalid && (
              <Alert severity="warning">
                Некорректных фраз: {plan.all.invalid}. Для них обращения не выполняются; причина
                будет показана у фразы.
              </Alert>
            )}
          </Stack>
        )}
        {planError && <Alert severity="error">{planError}</Alert>}
        {(planError || error) && (
          <Button
            onClick={() => {
              setPlanResult(undefined);
              setPlanRevision((value) => value + 1);
            }}
          >
            Обновить расчёт
          </Button>
        )}
      </Stack>
      <DialogActions sx={{ flexWrap: 'wrap', gap: 1, px: 3, pb: 2 }}>
        <Button disabled={busy} onClick={onClose}>
          Закрыть
        </Button>
        {editable && !google && !!plan?.failed?.queries && (
          <Button
            disabled={
              busy ||
              started ||
              !configured ||
              !!state?.latest?.pending ||
              !context ||
              !count ||
              !plan
            }
            onClick={() => collect('failed')}
          >
            Повторить ошибки · {plan.failed!.queries}
          </Button>
        )}
        {editable && !google && (
          <Button
            variant="outlined"
            disabled={
              busy || started || !configured || !!state?.latest?.pending || !plan?.missing?.queries
            }
            onClick={() => collect('missing')}
          >
            Собрать недостающие{plan?.missing ? ` · ${plan.missing.queries}` : ''}
          </Button>
        )}
        {editable && (
          <Button
            variant="contained"
            disabled={
              busy ||
              started ||
              !configured ||
              !!state?.latest?.pending ||
              !context ||
              (google && (!selectedRegion || count > GOOGLE_VOLUME_LIMIT)) ||
              !count ||
              !plan
            }
            onClick={() => collect('all')}
          >
            Собрать частотность · {count}
          </Button>
        )}
      </DialogActions>
      {historyOpen && (
        <FakesVolumeHistoryDialog
          workspaceId={workspaceId}
          editable={canStop}
          onChanged={onStopped}
          onClose={() => setHistoryOpen(false)}
        />
      )}
    </Dialog>
  );
}
